/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useCallback, useContext } from "react";
import DataTable, { Row } from "components/DataTable";
import apiService from "services/apiService";
import { FaEdit, FaEye, FaFolderOpen, FaComments } from "react-icons/fa";
import { AiOutlineExport } from "react-icons/ai";
import Modal from "components/Modal";
import ProjectDetail from "./ProjectDetail";
import AddProject from "./AddProject";
import EditProject from "./EditProject";
import GenericToaster from "components/Toaster";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { BannerContext } from "components/SideBar";
import { TruncatedText } from "pages/AdminConfig/components/TruncatedText";

export default function EmployeeList() {
  const [data, setData] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [viewData, setViewData] = useState();
  const [addproject, setAddProject] = useState(false);
  const [edit, setEdit] = useState(false);
  const [showSuccessToaster, setSuccessToaster] = useState(false);
  const [showErrorToaster, setErrorToaster] = useState(false);
  const [message, setMessage] = useState("Something went wrong");

  // Pagination & Search State
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();

  const getProjectList = useCallback(() => {
    setLoading(true);

    // Construct Query Parameters
    const query = new URLSearchParams();
    query.set("page", String(page));
    query.set("size", String(size));
    if (searchTerm.trim()) {
      query.set("search", searchTerm.trim());
    }

    apiService
      .get(`api/project?${query.toString()}`)
      .then((res) => {
        if (res.status === 200) {
          const payload = res.data;

          // Extract data from the new paginated JSON structure
          const newData = payload.content || [];

          const sortedData = newData.sort((a: any, b: any) =>
            (a.name || "").localeCompare(b.name || "")
          );

          setData(sortedData);

          // Force strict number typing to ensure the DataTable calculates pages correctly
          const parsedTotal = Number(payload.totalElements);
          setTotalElements(!isNaN(parsedTotal) ? parsedTotal : newData.length);
        }
      })
      .catch((err) => {
        console.error("Error fetching projects:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, size, searchTerm]);

  useEffect(() => {
    getProjectList();
  }, [getProjectList]);

  // Handlers for Pagination and Search
  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage - 1); // UI sends 1-based, API needs 0-based
  }, []);

  const handleItemsPerPageChange = useCallback((nextSize: number) => {
    setSize(nextSize);
    setPage(0); // Reset to first page when changing page size
  }, []);

  const handleSearchTermChange = useCallback((nextSearchTerm: string) => {
    setSearchTerm(nextSearchTerm);
    setPage(0);
  }, []);

  const { isBannerVisible } = useContext(BannerContext);
  const dynamicHeightClass = isBannerVisible
    ? "h-[calc(100vh-110px)] min-h-[300px]"
    : "h-[calc(100vh-70px)] min-h-[300px]";

  // Client-side export logic (exports current page)
  const handleExport = () => {
    if (!data || data.length === 0) {
      handleError("No data available to export.");
      return;
    }

    const exportData = data.map((row: any) => ({
      "Project": row.name || "-",
      Manager: row.manager || "-",
      "Size": row.teamSize || 0,
      "Client": row.clientContactName || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    worksheet["!cols"] = [{ wch: 25 }, { wch: 25 }, { wch: 15 }, { wch: 25 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");
    XLSX.writeFile(workbook, "Projects_List.xlsx");
  };

  const tableColumns = [
    {
      key: "name",
      label: "Project",
      bold: true,
      render: (_value: any, row: any) => {
        const projectName = row.name || "—";
        return (
          <TruncatedText
            text={projectName}
            // Adjust these widths based on how much space you want the project name to take
            className="w-[180px] sm:w-[180px] lg:w-[180px]"
          />
        );
      }
    },
    { key: "manager", label: "Manager", bold: true },
    { key: "teamSize", label: "Size", bold: true },
    { key: "clientContactName", label: "Client", bold: true },
    { key: "projectDetail", label: "Details", bold: true },
    {
      key: "actions",
      label: "Actions",
      excludeExport: true,
      actions: (row: any) => (
        <div className="flex flex-start">
          <span
            onClick={() => handleEdit(row)}
            className="btn-primary px-2 py-2 mr-2 rounded-lg cursor-pointer"
          >
            <FaEdit />
          </span>
          <span
            onClick={() => handleView(row)}
            className="btn-primary px-2 py-2 mr-2 rounded-lg cursor-pointer"
          >
            <FaEye />
          </span>
          <span
            onClick={() => handleNavigateDocuments(row)}
            className="btn-primary px-2 py-2 mr-2 rounded-lg text-blue-600 hover:bg-blue-100 dark:bg-blue-900/40 dark:text-blue-400 dark:hover:bg-blue-900/70 border border-blue-100 dark:border-blue-800/50 cursor-pointer"
          >
            <FaFolderOpen />
          </span>
        </div>
      ),
    },
  ];

  const handleView = (row: any): void => {
    setViewData(row);
    setOpenModal(true);
  };

  const handleEdit = (row: any): void => {
    setViewData(row);
    setEdit(true);
  };

  const handleNavigateDocuments = (row: any) => {
    const id = row.projectId || row.id;
    navigate(`/admin/projects/${id}/documents`, { state: { name: row.name } });
  };

  const handleAddfunction = () => {
    setAddProject(!addproject);
  };

  const handleSuccess = (msg: string) => {
    setMessage(msg);
    setSuccessToaster(true);
    getProjectList();
    window.dispatchEvent(new Event("refresh-user-data"));
  };

  const handleError = (errMessage: unknown) => {
    const safeMessage =
      typeof errMessage === "string"
        ? errMessage
        : typeof (errMessage as any)?.message === "string"
          ? (errMessage as any).message
          : "Something went wrong";
    setMessage(safeMessage);
    setErrorToaster(true);
  };

  const closeToaster = () => {
    setErrorToaster(false);
    setSuccessToaster(false);
  };

  return (
    <div className="text-slate-900 dark:text-white bg-white dark:bg-gray-800 transition-colors duration-300">
      <div className="bg-white dark:bg-gray-800">
        <DataTable
          heightClass={dynamicHeightClass}
          data={data}
          columns={tableColumns}
          loading={loading}
          onAddBtnClick={handleAddfunction}

          // --- Explicitly enable pagination flags ---
          serverSidePagination={true}

          // --- Server-side Pagination & Search Props ---
          itemsPerPage={size}
          currentPage={page + 1}
          totalItems={totalElements}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onSearchTermChange={handleSearchTermChange}

          preAddButtons={
            <div className="flex items-center">
              <button
                onClick={handleExport}
                title="Export Projects"
                type="button"
                className="inline-flex items-center justify-center h-[38px] px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-500 transition-all shadow-sm mr-4"
              >
                <AiOutlineExport size={18} className="mr-2" />
                <span>Export</span>
              </button>

              <button
                onClick={() => navigate("/admin/chat")}
                title="Project Chat"
                className="bg-primary h-[38px] text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2 hover:opacity-90 whitespace-nowrap mr-2"
              >
                <FaComments size={20} />
                <span className="hidden sm:inline">Project Chat</span>
              </button>
            </div>
          }
        />
      </div>

      {openModal && (
        <Modal isOpen={openModal} onClose={() => setOpenModal(false)} header="View Project Details">
          <div className="bg-white dark:bg-gray-800">
            {viewData && <ProjectDetail data={viewData} />}
          </div>
        </Modal>
      )}

      {addproject && (
        <Modal isOpen={addproject} onClose={() => setAddProject(false)} header="Add Project" size="xl">
          <div className="bg-white dark:bg-gray-800">
            <AddProject onClose={() => setAddProject(false)} onSuccess={handleSuccess} onError={handleError} />
          </div>
        </Modal>
      )}

      {edit && (
        <Modal isOpen={edit} onClose={() => setEdit(false)} header="Edit Project" size="xl">
          <div className="bg-white dark:bg-gray-800">
            <EditProject onClose={() => setEdit(false)} data={viewData} onSuccess={handleSuccess} onError={handleError} />
          </div>
        </Modal>
      )}

      {(showSuccessToaster || showErrorToaster) && (
        <GenericToaster
          message={message}
          onClose={closeToaster}
          open={showSuccessToaster || showErrorToaster}
          severity={showErrorToaster ? "error" : "info"}
        />
      )}
    </div>
  );
}